import * as React from 'react';
import {useDispatch} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import * as reduxApi from 'src/utils/redux-api-v2';
import type {
  Question,
  Option,
  Job,
  ResponseFieldName,
} from 'src/types/referral-external';
import {validateEmail} from 'src/utils/index';

import {getCountryCallingCode} from 'libphonenumber-js/max';
import {validatePhone, getCountryFromPhone} from 'src/utils/phone';
import {
  QUESTIONNAIRE_TYPES,
  COUNTRY_CODES,
  showLabelWithAsterisk,
} from 'src/utils/referral-external';
import {INPUT_TEXT_LIMIT} from 'src/utils/referral-v2';
import {
  RadioButton,
  RadioGroup,
  FormLabelSmall,
  Dropdown,
  Input,
  FileUpload,
  SubTitleLarge,
  Button,
  Toggle,
  Card,
  Panel,
  PanelBody,
  PanelHeader,
  type FileObject,
} from '@spaced-out/ui-design-system/lib/components/index';
import JobDetails from 'src/components/referral-external/jobs/job-details';
import JobListing from 'src/components/referral-external/jobs/job-listing';

import css from './form.css';


export const isQuestionVisible = (
  token: string,
  question: Question,
): boolean => {
  if (!token && question.is_visible === false) {
    return false;
  }
  if (
    token &&
    (!question.is_visible || question.candidate_visibility === 'hidden')
  ) {
    return false;
  }
  return true;
};

const isQuestionValid = (
  token: string,
  question: Question,
  value: string | string[],
) => {
  if (
    !question.optional &&
    ((token &&
      question.is_visible &&
      question.candidate_visibility !== 'hidden') ||
      (!token && question.is_visible)) &&
    !value &&
    question.response_field !== 'job_id'
  ) {
    return false;
  }
  return true;
};

export type QuestionComponentProps = {
  question: Question;
  onChange: (
    arg1: number,
    arg2: string,
    arg3?: string | string[] | null | undefined,
  ) => unknown;
  value: string | string[] | null | undefined;
  errors: Map<
    number,
    {
      message: string;
    }
  >;
  token?: string;
  referralLinkId: string;
};

export type TextInputQuestionProps = QuestionComponentProps;
const TextInputQuestion = ({
  question,
  value,
  onChange,
  errors,
  token,
}: TextInputQuestionProps): React.ReactElement<
  React.ComponentProps<React.ElementType>
> => {
  const {t} = useLabelTranslation();
  const getPlaceholder = (fieldName: ResponseFieldName) => {
    switch (fieldName) {
      case QUESTIONNAIRE_TYPES.referralEmail:
      case QUESTIONNAIRE_TYPES.referrerEmail:
        return t('Enter_Email', 'Enter Email');
      case QUESTIONNAIRE_TYPES.referralName:
      case QUESTIONNAIRE_TYPES.referrerName:
        return t('Enter_Name', 'Enter Name');
      default:
        return t('Enter_details', 'Enter details');
    }
  };
  const fieldError = errors.get(question.id);
  const error = Boolean(fieldError);
  const errorText = fieldError?.message;
  return (
    <div className={css.questionContainer}>
      <Input
        label={showLabelWithAsterisk(question.value, !question.optional)}
        placeholder={getPlaceholder(question.response_field)}
        value={Array.isArray(value) ? value.join('') : value ?? ''}
        onChange={(e) => {
          onChange(question.id, question.response_field, e.currentTarget.value);
        }}
        error={error}
        errorText={errorText}
        disabled={question.candidate_visibility !== 'editable' && !!token}
        maxLength={
          question.response_field === 'candidate_recommendation'
            ? INPUT_TEXT_LIMIT.form_recommendation
            : INPUT_TEXT_LIMIT.default
        }
      />
    </div>
  );
};
type JobRadioInputQuestionProps = QuestionComponentProps & {
  radioOptions?: Option[];
  title?: string;
};

const JobRadioInputQuestion = ({
  question,
  value,
  onChange,

  radioOptions = [
    {label: 'Email', key: 'Email'},
    {label: 'SMS', key: 'SMS'},
  ],

  token,
  referralLinkId,
  errors,
  title = '',
}: JobRadioInputQuestionProps): React.ReactElement<
  React.ComponentProps<React.ElementType>
> => {
  const {t, makeKey} = useLabelTranslation();
  const [isToggle, setIsToggle] = React.useState(false);
  const [jobDetailsOpen, setJobDetailsOpen] = React.useState(false);
  const [showJobsListing, setShowJobsListing] = React.useState(false);

  const [clickedJobId, setClickedJobId] = React.useState('');
  const [selectedJob, setSelectedJob] = React.useState<any>(null);

  const radioOptionsTranslated = radioOptions.map((option) => ({
    label: t(makeKey(option.label), option.label),
    key: option.key,
  }));

  React.useEffect(() => {
    if (value && typeof value === 'string' && token) {
      if (question.response_field === QUESTIONNAIRE_TYPES.reachOutToReferral) {
        // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<boolean>'.
        setIsToggle(value);
      }
      if (question.response_field === QUESTIONNAIRE_TYPES.job) {
        // @ts-expect-error - TS2345 - Argument of type '"true"' is not assignable to parameter of type 'SetStateAction<boolean>'.
        setIsToggle('true');
        setSelectedJob({id: value, title: selectedJob?.title || title});
      }
    }
  }, [value]);

  // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type. | TS7031 - Binding element 'title' implicitly has an 'any' type.
  const handleSelectJob = ({id, title}) => {
    onChange(question.id, question.response_field, id);
    setSelectedJob({id, title});
    setJobDetailsOpen(false);
    setShowJobsListing(false);
    setClickedJobId('');
  };

  const handleResetJob = () => {
    onChange(question.id, question.response_field, null);
    setSelectedJob(null);
  };
  const fieldError = errors.get(question.id);
  const error = Boolean(fieldError);
  return (
    <div className={css.questionContainer}>
      {question.response_field === QUESTIONNAIRE_TYPES.job && (
        <Card classNames={{wrapper: css.jobSelectInputBox}}>
          <div>
            <FormLabelSmall className={css.defaultMargin}>
              {question.value}
            </FormLabelSmall>
            <Toggle
              // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'boolean' and 'string' have no overlap.
              checked={isToggle === 'true'}
              onChange={({checked}) => {
                if (!checked) {
                  handleResetJob();
                }
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<boolean>'.
                setIsToggle(checked.toString());
              }}
            />
          </div>
          {/* @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'boolean' and 'string' have no overlap. */}
          {isToggle === 'true' &&
            question.response_field === QUESTIONNAIRE_TYPES.job &&
            (selectedJob ? (
              <Input
                classNames={{box: css.selectedJob}}
                value={selectedJob.title}
                iconRightName="xmark"
                onIconRightClick={() => handleResetJob()}
              />
            ) : (
              <Button
                iconRightName="chevron-right"
                type="tertiary"
                disabled={
                  question.candidate_visibility !== 'editable' && !!token
                }
                classNames={{wrapper: css.selectJob}}
                onClick={() => setShowJobsListing(true)}
              >
                {t('Select_Job', 'Select Job')}
              </Button>
            ))}
        </Card>
      )}
      {question.response_field === QUESTIONNAIRE_TYPES.reachOutToReferral && (
        <>
          {showLabelWithAsterisk(question.value, !question.optional)}
          <RadioGroup
            name="radio"
            align="horizontal-fluid"
            errorText={fieldError?.message}
            onChange={(value) => {
              onChange(question.id, question.response_field, value);
            }}
            // @ts-expect-error - TS2769 - No overload matches this call.
            value={value}
            error={error}
          >
            {radioOptionsTranslated.map((option) => (
              <RadioButton
                key={option.label}
                disabled={
                  question.candidate_visibility !== 'editable' && !!token
                }
                value={option.key}
                classNames={{wrapper: css.defaultMargin}}
              >
                {option.label}
              </RadioButton>
            ))}
          </RadioGroup>
        </>
      )}
      <Panel
        anchor="right"
        initialFocus={2}
        onClose={() => setShowJobsListing(false)}
        isOpen={showJobsListing}
        classNames={{content: css.panelWidth}}
      >
        <PanelHeader onCloseButtonClick={() => setShowJobsListing(false)}>
          <SubTitleLarge>{t('Job_List', 'Job List')}</SubTitleLarge>
        </PanelHeader>
        <PanelBody>
          <JobListing
            selectJob={setClickedJobId}
            setJobDetailsOpen={setJobDetailsOpen}
            handleSelectJob={handleSelectJob}
            referralLinkId={referralLinkId || ''}
          />
        </PanelBody>
      </Panel>
      <Panel
        anchor="right"
        initialFocus={2}
        onClose={() => setJobDetailsOpen(false)}
        isOpen={!!clickedJobId && jobDetailsOpen}
        classNames={{content: css.panelWidth}}
      >
        <PanelHeader
          onCloseButtonClick={() => {
            setJobDetailsOpen(false);
            setShowJobsListing(false);
          }}
          size="medium"
        >
          <div className={css.modalHeaderInfo}>
            <Button
              type="ghost"
              onClick={() => setJobDetailsOpen(false)}
              iconLeftName="chevron-left"
              classNames={{wrapper: css.button}}
            />
            <SubTitleLarge>{t('Job_Detail', 'Job Detail')}</SubTitleLarge>
          </div>
        </PanelHeader>
        <JobDetails
          setJobDetailsOpen={setJobDetailsOpen}
          handleSelectJob={handleSelectJob}
          jobId={clickedJobId}
        />
      </Panel>
    </div>
  );
};

type PhoneInputQuestionProps = QuestionComponentProps;
const PhoneInputQuestion = ({
  question,
  value,
  onChange,
  errors,
  token,
}: PhoneInputQuestionProps): React.ReactElement<
  React.ComponentProps<React.ElementType>
> => {
  const {t} = useLabelTranslation();
  const defaultSelectedCountryCode = COUNTRY_CODES.find(
    (country) => country.key === '+1',
  );

  let [countryCode, phone] = value ?? [defaultSelectedCountryCode?.key, ''];

  const fieldError = errors.get(question.id);
  const error = Boolean(fieldError);
  const errorText = fieldError?.message;

  if (value && typeof value === 'string' && value.toString().startsWith('+')) {
    const countryShortCode = getCountryFromPhone(value);
    if (countryShortCode) {
      // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'CountryCode'.
      countryCode = `+${getCountryCallingCode(countryShortCode)}`;
      phone = value.slice(countryCode.length);
    }
  }

  const selectedCountryCode =
    COUNTRY_CODES.find((country) => country.key === countryCode) ||
    defaultSelectedCountryCode;

  return (
    <div className={css.questionContainer}>
      {showLabelWithAsterisk(question.value, !question.optional)}
      <div className={css.phoneInput}>
        <Dropdown
          dropdownInputText={selectedCountryCode?.key}
          menu={{
            allowSearch: true,
            options: COUNTRY_CODES,
            selectedOption: selectedCountryCode,
            staticLabels: {
              SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
              RESULT: t('Result', 'Result'),
              RESULTS: t('Results', 'Results'),
            },
          }}
          onChange={(selectedOption) => {
            onChange(question.id, question.response_field, [
              selectedOption.key || '',
              phone as string,
            ]);
          }}
          classNames={{
            wrapper: css.countrySelect,
          }}
          disabled={question.candidate_visibility !== 'editable' && !!token}
        />
        <Input
          value={phone}
          placeholder={t('Enter_Phone', 'Enter Phone')}
          // @ts-expect-error - TS2769 - No overload matches this call.
          contextLabel={
            question.optional
              ? t('Optional', 'Optional')
              : t('Required', 'Required')
          }
          onChange={(e) => {
            onChange(question.id, question.response_field, [
              countryCode || '',
              e.target.value,
            ]);
          }}
          error={error}
          errorText={errorText}
          type="tel"
          disabled={question.candidate_visibility !== 'editable' && !!token}
        />
      </div>
    </div>
  );
};

const FileInputQuestion = ({
  question,
  errors,
  token,
  onChange,
  referralLinkId,
}: PhoneInputQuestionProps): React.ReactElement<
  React.ComponentProps<React.ElementType>
> => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const ref = React.useRef(null);

  const handleFileUpload = (question: Question, fileObject: FileObject) => {
    const fileType = fileObject.file.type.split('/')[1];
    const formData = new FormData();
    // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
    ref.current?.moveFileToProgress(30);
    const otherFields = {
      referral_link_id: referralLinkId,
      file_type: fileType,
    } as const;
    Object.keys(otherFields).forEach((key) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly referral_link_id: string; readonly file_type: string; }'.
      formData.append(key, otherFields[key]);
    });
    formData.append('file', fileObject.file);
    // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
    ref.current?.moveFileToProgress(fileObject.id, 60);
    dispatch(reduxApi.post(`referral/upload`, formData))
      .then((payload) => {
        // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
        ref.current?.moveFileToProgress(fileObject.id, 80);
        onChange(question.id, question.response_field, payload.file_path);
        // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
        ref.current?.moveFileToProgress(fileObject.id, 100);
        // @ts-expect-error - TS2339 - Property 'moveFileToSuccess' does not exist on type 'never'.
        ref.current?.moveFileToSuccess(fileObject.id, 'Successfully uploaded');
      })
      .catch((error) => {
        // @ts-expect-error - TS2339 - Property 'moveFileToReject' does not exist on type 'never'.
        ref.current?.moveFileToReject(
          fileObject.id,
          error.responseBody?.message,
        );
      });
  };

  const fieldError = errors.get(question.id);

  return (
    <div className={css.questionContainer}>
      {showLabelWithAsterisk(question.value, !question.optional)}
      <FileUpload
        label=""
        ref={ref}
        required={!question.optional}
        instruction={t('Drag_drop_the_file_here', 'Drag & drop the file here')}
        secondaryInstruction={t(
          'Types_pdf_doc_docx_txt_png_jpg_Max_size_5mb_Min_size_10kb',
          'Types: .pdf, .doc, .docx, .txt, .png, .jpg  Max. size: 5mb Min. size 10kb',
        )}
        maxSize={5000000} //in bytes
        accept={{
          'image/pdf': ['.pdf', '.doc', '.docx', '.txt', '.png', '.jpg'],
        }}
        onFileClear={() => {
          onChange(question.id, '');
        }}
        onValidFilesDrop={(file) => {
          handleFileUpload(question, file[0]);
        }}
        disabled={question.candidate_visibility !== 'editable' && !!token}
      />
      <FormLabelSmall color="danger">{fieldError?.message}</FormLabelSmall>
    </div>
  );
};

export const QuestionComponents = {
  text: TextInputQuestion,
  job_radio: JobRadioInputQuestion,
  phone: PhoneInputQuestion,
  file: FileInputQuestion,
  email: TextInputQuestion,
} as const;

export function validate(
  questions: Question[],
  answers: Map<number, string | null | undefined | string[]>,
  touched: Map<number, boolean>,
  token: string,
  t: (key: string, value: string) => string,
): Map<
  number,
  {
    message: string;
  }
> {
  const error = new Map();
  let referralEmail = '';
  let referrerEmail = '';
  questions
    .filter((question) => question.is_question_set)
    .forEach((question) => {
      const value = answers.get(question.id) || '';
      const isTouched = touched.get(question.id);

      if (question.response_field === QUESTIONNAIRE_TYPES.referralEmail) {
        // @ts-expect-error - TS2322 - Type 'string | string[]' is not assignable to type 'string'.
        referralEmail = value;
      }
      if (question.response_field === QUESTIONNAIRE_TYPES.referrerEmail) {
        // @ts-expect-error - TS2322 - Type 'string | string[]' is not assignable to type 'string'.
        referrerEmail = value;
      }
      if (
        !isQuestionValid(token || '', question, value) &&
        isTouched &&
        question.type === 'text'
      ) {
        error.set(question.id, {
          message: t('Please_provide_a_value', 'Please provide a value'),
        });
      }
      if (
        !isQuestionValid(token || '', question, value) &&
        isTouched &&
        question.response_field === QUESTIONNAIRE_TYPES.candidateResume
      ) {
        error.set(question.id, {
          message: t('Please_upload_a_resume', 'Please upload a resume'),
        });
      }
      if (
        !isQuestionValid(token || '', question, value) &&
        isTouched &&
        (question.response_field === QUESTIONNAIRE_TYPES.referrerName ||
          question.response_field === QUESTIONNAIRE_TYPES.referralName)
      ) {
        error.set(question.id, {
          message: `${t(
            'Please_enter_a_valid_name',
            'Please enter a valid name',
          )}.`,
        });
      }

      if (
        (!isQuestionValid(token || '', question, value) || value) &&
        question.type === 'email' &&
        isTouched
      ) {
        // @ts-expect-error - TS2345 - Argument of type 'string | string[]' is not assignable to parameter of type 'string'.
        if (!validateEmail(value)) {
          error.set(question.id, {
            message: `${t(
              'Please_enter_a_valid_email',
              'Please enter a valid email',
            )}.`,
          });
        }
      }
      if (
        (question.type === 'phone' &&
          isTouched &&
          !isQuestionValid(token || '', question, value[2])) ||
        (Array.isArray(value) && value[1])
      ) {
        let [countryCode, phone] = value ?? [COUNTRY_CODES[0].key, ''];
        const phoneNumberWithCountryCode = countryCode + phone || '+1';
        let countryShortCode = getCountryFromPhone(phoneNumberWithCountryCode);
        if (
          value &&
          typeof value === 'string' &&
          value.toString().startsWith('+')
        ) {
          countryShortCode = getCountryFromPhone(value);
          const countryCodeFromPhone = `+${getCountryCallingCode(
            // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'CountryCode'.
            countryShortCode,
          )}`;
          phone = value.slice(countryCodeFromPhone.length);
        }
        //we take answer as [countryCode,phoneNumber]
        if (!phone || !validatePhone(phone, countryShortCode || 'US')) {
          error.set(question.id, {
            message: `${t(
              'Please_provide_a_valid_phone_number',
              'Please provide a valid phone number',
            )}.`,
          });
        }
      }
    });
  const emailFiledId = questions.find(
    (question) => question.response_field === QUESTIONNAIRE_TYPES.referralEmail,
  )?.id;
  const phoneFieldId = questions.find(
    (question) => question.response_field === QUESTIONNAIRE_TYPES.referrerEmail,
  )?.id;
  if (validateEmail(referralEmail) && validateEmail(referrerEmail)) {
    if (referralEmail === referrerEmail) {
      error.set(emailFiledId, {
        message: `${t(
          'Referral_and_Referrer_email_id_can\'t_be_same',
          'Referral and Referrer email id can\'t be same',
        )}.`,
      });
      error.set(phoneFieldId, {
        message: `${t(
          'Referrer_and_Referrer_email_id_can\'t_be_same',
          'Referrer and Referrer email id can\'t be same',
        )}.`,
      });
    }
  }
  return error;
}

export function prepareReferralApiPayload(
  questions: Question[],
  answers: Map<number, string | null | undefined | string[]>,
  referral_link_id: string,
  jobs: Job[],
  utm_source?: string | null,
  utm_medium?: string | null,
): {
  [key: string]: Question | string;
} {
  const payload: Record<string, any> = {};
  questions.forEach((question) => {
    const value = answers.get(question.id) || '';
    /*** NOTE:(diwakersurya) this should not be done on the ui
     * ideally only job id should be passed from ui and backend
     * should derive the job_title and payout_amount from job id
     */
    if (question.response_field === 'job_id' && value) {
      const job = jobs.find((job) => job.id === value);
      if (job) {
        payload[question.response_field] = job.id;
        payload['job_title'] = job.title;
        payload['payout_amount'] = job.payout_amount;
      }
    } else if (
      question.response_field === 'candidate_contact' &&
      Array.isArray(value)
    ) {
      // filtering non empty value
      const updatedValue = value.filter((val) => val);
      payload[question.response_field] =
        // for candidate_contact the value will be pair of 2 element with country code and contact number
        updatedValue.length > 1 ? value.join('') : '';
    } else if (typeof value === 'string') {
      payload[question.response_field] = value;
    }
  });
  payload['referral_link_id'] = referral_link_id;
  if (utm_source) {
    payload['utm_source'] = utm_source;
  }
  if (utm_medium) {
    payload['utm_medium'] = utm_medium;
  }
  return payload;
}
